import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone } from "lucide-react";
import { MaskedLines } from "./Motion";
import { GoldButton, GhostButton } from "./Buttons";
import { ZodiacWheel } from "./ZodiacWheel";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { IMAGES, PHONE_TEL, WHATSAPP_URL } from "../../lib/site";

const LINES = [
  { text: "Discover Guidance," },
  { text: "Clarity & Direction", className: "italic gold-text" },
  { text: "Through Astrology" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 70, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 70, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden pb-20 pt-32">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Starry night sky over the mountains"
          className="h-[115%] w-full object-cover"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.55 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(76,29,149,0.35),transparent_60%),radial-gradient(ellipse_at_50%_0%,rgba(212,175,55,0.14),transparent_55%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#07040D]/60 via-[#07040D]/40 to-[#07040D]" />
      <div className="stars twinkle absolute inset-0" />

      <div className="pointer-events-none absolute -right-[35%] top-24 w-[115vw] opacity-30 sm:-right-[20%] sm:w-[80vw] lg:right-[2%] lg:top-1/2 lg:w-[42vw] lg:max-w-[640px] lg:-translate-y-1/2 lg:opacity-100" style={{ perspective: 1200 }}>
        <motion.div
          style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
          initial={{ opacity: 0, scale: 0.8, rotate: -40 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        >
          <ZodiacWheel className="h-auto w-full drop-shadow-[0_0_60px_rgba(212,175,55,0.25)]" />
        </motion.div>
      </div>

      <motion.div style={{ y: textY }} className="relative mx-auto w-full max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          data-testid="hero-business-name" className="font-cinzel flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] tracking-[0.3em] text-[#F3E5AB] sm:text-xs">
          <span>ASTROLOGER &amp; SPIRITUAL HEALERS</span>
          <span className="h-px w-8 bg-[#D4AF37]/60" />
          <span className="text-[#D4AF37]">CALGARY, ALBERTA</span>
        </motion.p>
        <h1 data-testid="hero-headline" className="font-display mt-7 max-w-5xl text-[44px] leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[96px] xl:text-[108px]">
          <MaskedLines lines={LINES} delay={0.35} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg">
          Personal astrology, psychic and tarot readings, and gentle spiritual healing for life's real questions —
          offered privately in Calgary, in person or by phone.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <GoldButton to="/book" testId="hero-book-button">Book an Appointment</GoldButton>
          <GhostButton href={PHONE_TEL} testId="hero-call-button" icon={Phone}>Call Now</GhostButton>
          <GhostButton href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" testId="hero-whatsapp-button" icon={WhatsAppIcon}>WhatsApp</GhostButton>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 text-[10px] tracking-[0.35em] text-slate-400 sm:flex">
        SCROLL
        <span className="relative h-12 w-px overflow-hidden bg-[#D4AF37]/20">
          <motion.span className="absolute inset-x-0 top-0 h-1/2 bg-[#D4AF37]" animate={{ y: ["-100%", "200%"] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
        </span>
      </motion.div>
    </section>
  );
};
